import { BaseAPI } from "@utils/base-api";
import { PouchSize } from "@./cat-calculations";
import { AxiosRequestConfig } from "axios";
import {
  AddFreshSubscriptionRequestDto,
  AddFreshSubscriptionResponseDto,
  GetLitterPriceInfoResponseDto,
  UpdateScoopFrequencyAndQuantityRequestDto,
  UpdateScoopFrequencyAndQuantityResponseDto,
} from "@./types/subscriptions";

import { PricingPlan } from "@/primary-models";

import {
  SUBSCRIPTIONS_CUSTOMER_ADD_FRESH_PATH,
  SUBSCRIPTIONS_CUSTOMER_CANCEL_SUBSCRIPTION_PATH,
  SUBSCRIPTIONS_CUSTOMER_GET_OVERVIEW_PATH,
  SUBSCRIPTIONS_CUSTOMER_GET_POSSIBLE_DELIVERY_DATES_PATH,
  SUBSCRIPTIONS_CUSTOMER_REACTIVATE_SUBSCRIPTION_PATH,
  SUBSCRIPTIONS_CUSTOMER_UPDATE_DELIVERY_DATE_PATH,
  SUBSCRIPTIONS_CUSTOMER_UPDATE_EXTRAS_PATH,
  SUBSCRIPTIONS_CUSTOMER_UPDATE_FRESH_RECIPES_PATH,
  SUBSCRIPTIONS_CUSTOMER_UPDATE_MEAL_PLAN_PATH,
  SUBSCRIPTIONS_CUSTOMER_UPDATE_SCOOP_PATH,
  SUBSCRIPTIONS_CUSTOMER_UPSELL_NIBBLES_PATH,
  SUBSCRIPTIONS_PUBLIC_GET_FRESH_PRICING_INFO_PATH,
  SUBSCRIPTIONS_PUBLIC_GET_LITTER_PRICING_INFO_PATH,
} from "./endpoint-paths";
import {
  IFreshPricingPlan,
  TFreshMealPlanPricingMap,
  ITraySize,
  IUpdateMealPlanOrTraySizeRequest,
  IUpdateMealPlanOrTraySizeResponse,
} from "./types/meal-plan.types";
import {
  ICancelSubscriptionRequest,
  ICancelSubscriptionResponse,
  IReactivateSubscriptionRequest,
  IReactivateSubscriptionResponse,
  ISubscriptionOverview,
} from "./types/subscriptions.types";
import {
  IUpdateDeliveryDateReason,
  IUpdateDeliveryDateRequest,
  IUpdateDeliveryDateResponse,
} from "./types/delivery.types";
import {
  IUpdateRecipesRequest,
  IUpdateRecipesResponse,
} from "./types/fresh.types";
import {
  INibblesUpsellRequest,
  INibblesUpsellResponse,
  IUpdateExtraItemRequest,
  IUpdateExtraItemResponse,
} from "./types/extra.types";
import {
  IPossibleDeliveryDatesQuery,
  IPossibleDeliveryDatesResponse,
} from "./types/possible-delivery-dates.types";
import { isTrialPlan } from "./subscription-utils";
import { LitterProduct } from "./types/litter.types";

export class SubscriptionsAPI extends BaseAPI {

  public async getPossibleDeliveryDatesSub(
    request: IPossibleDeliveryDatesQuery,
  ): Promise<IPossibleDeliveryDatesResponse> {
    const params = new URLSearchParams();
    Object.entries(request).forEach(([ key, value ]) => (Array.isArray(value)
      ? value.forEach((v) => params.append(key, v))
      : params.append(key, value)));
    const response = await this.axios.get(
      SUBSCRIPTIONS_CUSTOMER_GET_POSSIBLE_DELIVERY_DATES_PATH,
      { params },
    );
    return response.data;
  }

  /**
   * @param includeTrialPlans Whether to include trial plans in the response. Defaults to false.
   * @returns A mapping of pricing plans to their plan pricing details.
   */
  public async getFreshMealPlanPricingInfoMap({
    includeTrialPlans = false,
  }: {
    includeTrialPlans?: boolean,
  }): Promise<TFreshMealPlanPricingMap> {
    const { pricingPlans } = await this.getWithTransform<{ pricingPlans: TFreshMealPlanPricingMap }>(
      SUBSCRIPTIONS_PUBLIC_GET_FRESH_PRICING_INFO_PATH,
      {
        params: {
          all: includeTrialPlans,
        },
      },
      {
        decimalFields: [ "price" ],
      },
    );

    return pricingPlans;
  }

  /**
   * @param includeTrialPlans Whether to include trial plans in the response. Defaults to false.
   * @returns An array of meal plans with their price information.
   */
  public async getAvailableMealPlans({
    includeTrialPlans = false,
  }: {
    includeTrialPlans?: boolean,
  } = {}): Promise<IFreshPricingPlan[]> {
    const pricingPlans = await this.getFreshMealPlanPricingInfoMap({
      includeTrialPlans,
    });

    return Object.entries(pricingPlans).map(([ pricingPlan, pricingDetails ]) => ({
      ...pricingDetails,
      pricingPlan: pricingPlan as PricingPlan,
    }));
  }

  /**
   * @param pricingPlan The pricing plan to get the tray sizes for.
   * @returns A list of tray sizes with their price information.
   */
  public async getAvailableTraySizes(pricingPlan: PricingPlan): Promise<ITraySize[]> {
    const pricingPlans = await this.getFreshMealPlanPricingInfoMap({
      includeTrialPlans: isTrialPlan(pricingPlan),
    });

    return Object.entries(pricingPlans[pricingPlan].traySize).map(([ traySize, traySizeDetails ]) => ({
      traySize: traySize as PouchSize,
      ...traySizeDetails,
    }));
  }

  /**
   * @returns An overview of all subscriptions for the logged in user
   */
  public async getSubscriptionsOverview(config?: AxiosRequestConfig): Promise<ISubscriptionOverview> {
    const response = await this.axios.get(
      SUBSCRIPTIONS_CUSTOMER_GET_OVERVIEW_PATH,
      config,
    );
    return response.data;
  }

  /**
   * @param newDeliveryDate The new delivery date to set.
   * @param subscriptionIds The ids of the subscriptions to update.
   * @param reason Optional. The reason for the update.
   * @returns The updated subscriptions and delivery dates.
   */
  public async updateDeliveryDate({
    newDeliveryDate,
    subscriptionIds,
    reason,
  }:{
    newDeliveryDate: string,
    subscriptionIds: number[],
    reason?: IUpdateDeliveryDateReason,
  }): Promise<IUpdateDeliveryDateResponse> {
    const request: IUpdateDeliveryDateRequest = {
      newDeliveryDate,
      subscriptionIds,
      reason,
    };

    const result = await this.axios.patch(SUBSCRIPTIONS_CUSTOMER_UPDATE_DELIVERY_DATE_PATH, request);

    return result.data;
  }

  /**
   * Update the recipes for a given fresh subscription.
   * Will check for allergies and ensure the total quantity
   * of recipes is equal to the quantity of recipes expected
   * for the pricing plan.
   *
   * To be used by customers or CX via impersonation.
   *
   * @param request The request body containing the subscription id
   * and a map of recipes to quantities
   * @returns The updated recipes.
   */
  public async updateFreshRecipes(request: IUpdateRecipesRequest): Promise<IUpdateRecipesResponse> {
    const result = await this.axios.put(SUBSCRIPTIONS_CUSTOMER_UPDATE_FRESH_RECIPES_PATH, request);

    return result.data;
  }

  /**
   * Upsell nibbles to a fresh subscription and applies the relevant discount.
   *
   * To be used by customers or CX via impersonation.
   *
   * @param request The request body containing the user id, the product to upsell and the endpoint.
   * @returns The subscription id to which the product was added.
   */
  public async upsellNibbles(request: INibblesUpsellRequest): Promise<INibblesUpsellResponse> {
    const result = await this.axios.patch(SUBSCRIPTIONS_CUSTOMER_UPSELL_NIBBLES_PATH, request);

    return result.data;
  }

  /**
   * Change the quantity of nibbles or sprinkles for
   * the given subscription.
   *
   * To be used by customers or CX via impersonation.
   *
   * @param request The request body containing the subscription id and a record of the items to update.
   * @returns The updated items.
   */
  public async updateExtras(request: IUpdateExtraItemRequest): Promise<IUpdateExtraItemResponse> {
    const result = await this.axios.patch(SUBSCRIPTIONS_CUSTOMER_UPDATE_EXTRAS_PATH, request);

    return result.data;
  }

  /**
   * Update the meal plan and/or tray size for a given subscription.
   *
   * To be used by customers or CX via impersonation.
   *
   * @param request The request body containing an array of subscriptions to update,
   * each with the subscription id and the meal plan and/or tray size to update.
   * @returns The updated meal plan and/or tray size.
   */
  public async updateMealPlanOrTraySize(
    request: IUpdateMealPlanOrTraySizeRequest,
  ): Promise<IUpdateMealPlanOrTraySizeResponse> {
    const result = await this.axios.patch(SUBSCRIPTIONS_CUSTOMER_UPDATE_MEAL_PLAN_PATH, request);

    return result.data;
  }

  /** Cancel the given subscriptions
   *
   * To be used by customers or CX via impersonation.
   *
   * @param request The request body containing the reason for cancelling,
   * and an array of subscription IDs to cancel.
   * @returns The updated meal plan and/or tray size.
   */
  public async cancelSubscriptions(
    request: ICancelSubscriptionRequest,
  ): Promise<ICancelSubscriptionResponse | undefined> {
    const result = await this.axios.patch(SUBSCRIPTIONS_CUSTOMER_CANCEL_SUBSCRIPTION_PATH, request);

    return result.data;
  }

  /** Reactivate the given subscriptions
   *
   * To be used by customers or CX via impersonation.
   *
   * @param request The request body containing a discount to be applied
   * (does not fail if discount already applied),
   * and an array of subscription to reactivate
   * (with ID, delivery date and optionally pricing plan).
   * @returns The updated meal plan and/or tray size.
   */
  public async reactivateSubscriptions(
    request: IReactivateSubscriptionRequest,
  ): Promise<IReactivateSubscriptionResponse | undefined> {
    const result = await this.axios.patch(SUBSCRIPTIONS_CUSTOMER_REACTIVATE_SUBSCRIPTION_PATH, request);

    return result.data;
  }

  /** Update the frequency and quantity of a scoop subscription.
   *
   * To be used by customer or CX via impersonation.
   *
   * @param request The request body containing the frequency in
   * weeks and/or the quantity.
   * @returns The subscription's frequency and cadence after it was updated.
   * */
  public async changeScoopFrequencyAndQuantity(
    request: UpdateScoopFrequencyAndQuantityRequestDto,
  ): Promise<UpdateScoopFrequencyAndQuantityResponseDto | undefined> {
    const result = await this.axios.patch(SUBSCRIPTIONS_CUSTOMER_UPDATE_SCOOP_PATH, request);

    return result.data;
  }

  public async addFreshSubscription(
    request: AddFreshSubscriptionRequestDto,
  ): Promise<AddFreshSubscriptionResponseDto> {
    const result = await this.axios.post(`${SUBSCRIPTIONS_CUSTOMER_ADD_FRESH_PATH}`, request);

    return result.data;
  }

  public async getLitterPricingInfo(product: LitterProduct): Promise<GetLitterPriceInfoResponseDto> {
    const result = await this.axios.get(`${SUBSCRIPTIONS_PUBLIC_GET_LITTER_PRICING_INFO_PATH}?product=${product}`);

    return result.data;
  }

}
